import React from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import './WhyTecnivorns.css';

const ADVANTAGES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
        <polyline points="17 6 23 6 23 12"/>
      </svg>
    ),
    title: 'Faster Operations',
    metric: '60%',
    metricLabel: 'reduction in admin time',
    desc: 'Eliminate manual paperwork and repetitive data entry. Automated workflows handle scheduling, billing, and record-keeping so your staff focuses on patient care.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    title: 'Better Patient Experience',
    metric: '3x',
    metricLabel: 'faster consultation flow',
    desc: 'Patients spend less time waiting and more time receiving care. Online booking, instant prescriptions, and seamless pharmacy processing delight every visitor.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <ellipse cx="12" cy="5" rx="9" ry="3"/>
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
      </svg>
    ),
    title: 'Centralized Records',
    metric: '100%',
    metricLabel: 'digital, zero paper',
    desc: 'All patient records, prescriptions, lab reports, and billing data in a single, searchable system. Access any patient\'s complete history in under 3 seconds.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    title: 'Improved Revenue Tracking',
    metric: '28%',
    metricLabel: 'avg. revenue increase',
    desc: 'Real-time dashboards show collection trends, outstanding dues, and department-wise performance. Never miss billing an item again with integrated prescription-to-billing flow.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <rect x="2" y="7" width="20" height="14"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
        <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
      </svg>
    ),
    title: 'Reduced Administrative Work',
    metric: '80%',
    metricLabel: 'fewer manual tasks',
    desc: 'Automation handles appointment reminders, follow-up alerts, inventory reorder notifications, and report generation — freeing your front desk for patients.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
        <polyline points="2 17 12 22 22 17"/>
        <polyline points="2 12 12 17 22 12"/>
      </svg>
    ),
    title: 'Scalable Architecture',
    metric: '∞',
    metricLabel: 'branches supported',
    desc: 'Start with a single clinic and grow to a multi-location hospital chain without changing your system. Tecnivorns HMS scales with your ambitions.',
  },
];

const WhyTecnivorns = () => {
  const [ref, isVisible] = useScrollReveal(0.08);

  return (
    <section className="why-section section" id="why" ref={ref}>
      <div className="container">
        <div className={`section-header reveal${isVisible ? ' visible' : ''}`}>
          <span className="section-tag">Why Choose Us</span>
          <h2 className="section-title">
            Why Hospitals Choose<br />
            <span className="why-title-accent">Tecnivorns HMS</span>
          </h2>
          <p className="section-subtitle">
            Healthcare operators across India consistently choose Tecnivorns over alternatives for its speed, reliability, and healthcare-specific depth.
          </p>
        </div>

        <div className="why-grid">
          {ADVANTAGES.map((adv, i) => (
            <div
              key={adv.title}
              className={`why-card reveal delay-${(i % 3) + 1}${isVisible ? ' visible' : ''}`}
            >
              <div className="why-card__icon-row">
                <div className="why-card__icon">{adv.icon}</div>
                <div className="why-card__metric-wrap">
                  <span className="why-card__metric">{adv.metric}</span>
                  <span className="why-card__metric-label">{adv.metricLabel}</span>
                </div>
              </div>
              <h3 className="why-card__title">{adv.title}</h3>
              <p className="why-card__desc">{adv.desc}</p>
            </div>
          ))}
        </div>

        {/* Comparison callout */}
        <div className={`why-comparison reveal delay-3${isVisible ? ' visible' : ''}`}>
          <div className="why-comparison__label">Compared to traditional methods and legacy systems</div>
          <div className="why-comparison__items">
            {[
              { label: 'Setup Time', them: '3–6 months', us: '< 2 weeks' },
              { label: 'Training Required', them: 'Extensive', us: 'Minimal' },
              { label: 'Paper Dependency', them: 'High', us: 'Zero' },
              { label: 'Multi-branch Support', them: 'Limited', us: 'Unlimited' },
            ].map((row) => (
              <div key={row.label} className="comparison-row">
                <span className="comp-label">{row.label}</span>
                <span className="comp-them">Legacy: {row.them}</span>
                <span className="comp-us">Tecnivorns: {row.us}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyTecnivorns;
